'use client';

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, Smartphone, LayoutTemplate, Sparkles, 
  Instagram, Twitter, Youtube, Linkedin, Github, Globe, 
  Plus, Trash2, CheckCircle2, UserCircle, Palette, Settings2, Trash
} from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { toast } from 'sonner'

type BioLink = {
  id: string
  title: string
  url: string
}

type Template = {
  id: string
  label: string
  link_color: string
  theme: 'dark' | 'light'
  bg_type: 'solid' | 'gradient'
  bg_color_1: string
  bg_color_2: string
  button_style: 'solid' | 'outline' | 'soft'
  button_radius: 'none' | 'md' | 'xl' | 'full'
  text_color: string
}

const TEMPLATES: Template[] = [
  { id: 'midnight', label: 'Midnight', link_color: '#3B82F6', theme: 'dark', bg_type: 'solid', bg_color_1: '#000000', bg_color_2: '', button_style: 'solid', button_radius: 'xl', text_color: '#ffffff' },
  { id: 'galaxy', label: 'Galaxy', link_color: '#8B5CF6', theme: 'dark', bg_type: 'gradient', bg_color_1: '#0a0015', bg_color_2: '#0f0030', button_style: 'solid', button_radius: 'full', text_color: '#ffffff' },
  { id: 'obsidian', label: 'Obsidian', link_color: '#64748B', theme: 'dark', bg_type: 'solid', bg_color_1: '#0f172a', bg_color_2: '', button_style: 'outline', button_radius: 'none', text_color: '#94a3b8' },
  { id: 'forest', label: 'Forest', link_color: '#84CC16', theme: 'dark', bg_type: 'gradient', bg_color_1: '#040d00', bg_color_2: '#071500', button_style: 'soft', button_radius: 'xl', text_color: '#a3e635' },
  { id: 'sakura', label: 'Sakura', link_color: '#EC4899', theme: 'light', bg_type: 'gradient', bg_color_1: '#fff0f5', bg_color_2: '#fce7f3', button_style: 'soft', button_radius: 'full', text_color: '#db2777' },
  { id: 'monochrome', label: 'Monochrome', link_color: '#FFFFFF', theme: 'dark', bg_type: 'solid', bg_color_1: '#000000', bg_color_2: '', button_style: 'outline', button_radius: 'none', text_color: '#ffffff' },
]

const ACCENT_COLORS = [
  '#3B82F6', '#8B5CF6', '#EC4899', '#F97316', '#10B981',
  '#EAB308', '#64748B', '#FFFFFF', '#000000',
]

export default function LinkInBioClient() {
  const [activeTab, setActiveTab] = useState<'links' | 'appearance' | 'settings'>('links')
  
  // Profile settings state
  const [displayName, setDisplayName] = useState('@clark_kent')
  const [bio, setBio] = useState('Journalist, Designer & Writer')
  const [themeMode, setThemeMode] = useState<'dark' | 'light'>('dark')
  
  // Appearance state
  const [linkColor, setLinkColor] = useState('#3B82F6')
  const [bgType, setBgType] = useState<'solid' | 'gradient'>('solid')
  const [bgColor1, setBgColor1] = useState('#000000')
  const [bgColor2, setBgColor2] = useState('#111827')
  const [buttonStyle, setButtonStyle] = useState<'solid' | 'outline' | 'soft'>('solid')
  const [buttonRadius, setButtonRadius] = useState<'none' | 'md' | 'xl' | 'full'>('xl')
  const [textColor, setTextColor] = useState('#ffffff')

  // Links state
  const [bioLinks, setBioLinks] = useState<BioLink[]>([
    { id: '1', title: 'Portfolio & Work', url: 'https://clark.kent' },
    { id: '2', title: 'My Digital Products', url: 'https://lnk.ly/kent-shop' },
    { id: '3', title: 'Join Newsletter', url: 'https://lnk.ly/kent-news' },
  ])
  const [newTitle, setNewTitle] = useState('')
  const [newUrl, setNewUrl] = useState('')

  // Social Links state
  const [socialLinks, setSocialLinks] = useState({
    twitter: 'clark_kent',
    instagram: 'clark_kent',
    youtube: '',
    linkedin: 'clark-kent',
    github: '',
    website: 'clark.kent',
  })

  const handleApplyTemplate = (tpl: Template) => {
    setLinkColor(tpl.link_color)
    setThemeMode(tpl.theme)
    setBgType(tpl.bg_type)
    setBgColor1(tpl.bg_color_1)
    if (tpl.bg_color_2) setBgColor2(tpl.bg_color_2)
    setButtonStyle(tpl.button_style)
    setButtonRadius(tpl.button_radius)
    setTextColor(tpl.text_color)
    toast.success(`Template "${tpl.label}" loaded successfully!`)
  }

  const handleAddLink = () => {
    if (!newTitle || !newUrl) {
      toast.error('Please specify both a link title and a destination URL')
      return
    }
    let formattedUrl = newUrl.trim()
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = 'https://' + formattedUrl
    }
    const newLink: BioLink = {
      id: Date.now().toString(),
      title: newTitle,
      url: formattedUrl
    }
    setBioLinks([...bioLinks, newLink])
    setNewTitle('')
    setNewUrl('')
    toast.success('Link block added!')
  }

  const handleDeleteLink = (id: string) => {
    setBioLinks(bioLinks.filter(l => l.id !== id))
    toast.success('Link block deleted')
  }

  // Preview styling helpers
  const isLight = themeMode === 'light'
  const previewBg: React.CSSProperties = bgType === 'gradient' && bgColor1 && bgColor2
    ? { background: `linear-gradient(to bottom, ${bgColor1}, ${bgColor2})` }
    : { backgroundColor: bgColor1 }

  const getPreviewButtonStyle = (): React.CSSProperties => {
    if (buttonStyle === 'outline') {
      return { border: `2px solid ${linkColor}`, backgroundColor: 'transparent', color: textColor }
    }
    if (buttonStyle === 'soft') {
      return { backgroundColor: `${linkColor}20`, color: linkColor }
    }
    return { backgroundColor: linkColor, color: isLight && linkColor === '#FFFFFF' ? '#000' : '#fff' }
  }

  const previewRadius = buttonRadius === 'none' ? '0' : buttonRadius === 'md' ? '8px' : buttonRadius === 'xl' ? '16px' : '999px'

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 max-w-[1400px] mx-auto z-10 flex flex-col items-center">
        <motion.div 
          className="text-center space-y-6 max-w-3xl"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center gap-2 justify-center">
            <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              Linktree Alternative
            </span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.1]">
            Link-in-Bio Builder<br />
            <span className="text-gray-500">Engage your audience with</span><br />
            One Bio Link
          </motion.h1>

          <motion.p variants={itemVariants} className="text-base sm:text-lg text-white/50 font-light max-w-xl mx-auto leading-relaxed">
            Consolidate your social presence, sell digital items, receive responses, and capture emails with a custom link-in-bio page built in seconds.
          </motion.p>
        </motion.div>

        {/* Dynamic Split Screen Mockup (Dashboard Replication) */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="w-full max-w-5xl mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
        >
          {/* Left Panel: Builder Options */}
          <div className="lg:col-span-7 bg-[#0a0a0a]/80 border border-white/10 rounded-3xl backdrop-blur-xl shadow-[0_0_80px_rgba(0,102,255,0.05)] overflow-hidden">
            {/* Header / Brand info */}
            <div className="px-6 py-4 border-b border-white/[0.08] flex items-center justify-between bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold uppercase tracking-wider">Bio-Page Editor</span>
              </div>
              <div className="flex gap-1.5 items-center">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] font-mono text-white/40 uppercase">Connected</span>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-white/[0.08] px-4 pt-3 bg-white/[0.01]">
              {[
                { id: 'links', label: 'Links & Profile' },
                { id: 'appearance', label: 'Appearance' },
                { id: 'settings', label: 'Social & Geo' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as 'links' | 'appearance' | 'settings')}
                  className={`flex-1 pb-3 text-xs font-bold uppercase tracking-wider transition-all border-b-2 ${
                    activeTab === tab.id 
                      ? 'text-white border-primary' 
                      : 'text-white/40 border-transparent hover:text-white/60'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-6">
              <AnimatePresence mode="wait">
                
                {/* ════ LINKS TAB ════ */}
                {activeTab === 'links' && (
                  <motion.div
                    key="tab-links"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="space-y-6"
                  >
                    {/* Profile details */}
                    <div className="space-y-4 p-4 bg-white/[0.01] border border-white/5 rounded-2xl">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-white/50 flex items-center gap-1.5">
                        <UserCircle className="w-4 h-4 text-primary" /> Profile details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-white/40 uppercase">Display Name</label>
                          <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs focus:outline-none focus:border-primary/50 text-white font-medium"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-mono text-white/40 uppercase font-light">Short Bio</label>
                          <input
                            type="text"
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs focus:outline-none focus:border-primary/50 text-white/80 font-light"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <span className="text-xs text-white/60">Base Page Mode</span>
                        <div className="flex gap-1.5">
                          {['dark', 'light'].map(m => (
                            <button
                              key={m}
                              type="button"
                              onClick={() => {
                                setThemeMode(m as 'dark' | 'light')
                                setBgColor1(m === 'dark' ? '#000000' : '#ffffff')
                                setTextColor(m === 'dark' ? '#ffffff' : '#000000')
                              }}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all capitalize ${
                                themeMode === m ? 'bg-primary text-white' : 'bg-white/5 text-white/40'
                              }`}
                            >
                              {m}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Links manager list */}
                    <div className="space-y-4">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-white/50">Custom Link Blocks</h3>
                      <div className="space-y-2">
                        {bioLinks.map(link => (
                          <div key={link.id} className="flex items-center justify-between p-3.5 bg-white/[0.01] border border-white/5 rounded-xl gap-3">
                            <div className="truncate">
                              <p className="text-xs font-bold truncate">{link.title}</p>
                              <p className="text-[9px] font-mono text-white/30 truncate mt-0.5">{link.url}</p>
                            </div>
                            <button 
                              type="button"
                              onClick={() => handleDeleteLink(link.id)}
                              className="p-1.5 text-white/30 hover:text-red-400 hover:bg-white/5 rounded-lg transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Add block */}
                      <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl space-y-3">
                        <p className="text-[10px] font-mono text-white/40 uppercase">Add Smart Content Block</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input
                            type="text"
                            placeholder="Title (e.g. Subscribe on YouTube)"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs focus:outline-none focus:border-primary/60 transition-all"
                          />
                          <input
                            type="text"
                            placeholder="URL (e.g. youtube.com/c/yourchannel)"
                            value={newUrl}
                            onChange={(e) => setNewUrl(e.target.value)}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs focus:outline-none focus:border-primary/60 transition-all"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddLink}
                          className="w-full py-2 bg-white text-black hover:bg-white/90 font-bold rounded-lg text-xs transition-all flex items-center justify-center gap-1 active:scale-95"
                        >
                          <Plus className="w-3.5 h-3.5" /> Insert Link Block
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* ════ APPEARANCE TAB ════ */}
                {activeTab === 'appearance' && (
                  <motion.div
                    key="tab-appearance"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="space-y-6"
                  >
                    {/* Templates list */}
                    <div className="space-y-3">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-white/50 flex items-center gap-1.5">
                        <Palette className="w-4 h-4 text-primary" /> Preset templates
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {TEMPLATES.map(tpl => (
                          <button
                            key={tpl.id}
                            type="button"
                            onClick={() => handleApplyTemplate(tpl)}
                            className="p-3 bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 hover:border-white/10 rounded-xl text-left transition-all group flex flex-col justify-between"
                          >
                            <span className="text-xs font-bold text-white group-hover:text-primary transition-colors">{tpl.label}</span>
                            <span className="text-[8px] font-mono text-white/30 uppercase mt-1">
                              {tpl.button_style} • {tpl.button_radius}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Colors & Button Styles */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <label className="text-[10px] font-mono text-white/40 uppercase block">Accent Color</label>
                        <div className="flex flex-wrap gap-2">
                          {ACCENT_COLORS.map(hex => (
                            <button
                              key={hex}
                              type="button"
                              onClick={() => setLinkColor(hex)}
                              className={`w-6 h-6 rounded-full border transition-all active:scale-90 flex-shrink-0 ${
                                linkColor === hex ? 'ring-2 ring-primary ring-offset-2 ring-offset-black' : 'border-white/10'
                              }`}
                              style={{ backgroundColor: hex === 'transparent' ? '#333' : hex }}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label className="text-[10px] font-mono text-white/40 uppercase block">Text Color</label>
                        <div className="flex gap-2">
                          {['#ffffff', '#000000', '#94a3b8'].map(hex => (
                            <button
                              key={hex}
                              type="button"
                              onClick={() => setTextColor(hex)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-mono border transition-all ${
                                textColor === hex ? 'border-primary bg-primary/10 text-white' : 'border-white/5 bg-white/[0.02] text-white/50'
                              }`}
                            >
                              {hex}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Button style */}
                      <div className="space-y-2">
                        <label className="text-[10px] font-mono text-white/40 uppercase">Button Shape Style</label>
                        <select
                          value={buttonStyle}
                          onChange={(e) => setButtonStyle(e.target.value as any)}
                          className="w-full px-3 py-2 bg-[#0e0e0e] border border-white/10 rounded-xl text-xs focus:outline-none"
                        >
                          <option value="solid">Solid Color</option>
                          <option value="outline">Outline Border</option>
                          <option value="soft">Soft Tint</option>
                        </select>
                      </div>

                      {/* Button radius */}
                      <div className="space-y-2">
                        <label className="text-[10px] font-mono text-white/40 uppercase">Button Rounded Corners</label>
                        <select
                          value={buttonRadius}
                          onChange={(e) => setButtonRadius(e.target.value as any)}
                          className="w-full px-3 py-2 bg-[#0e0e0e] border border-white/10 rounded-xl text-xs focus:outline-none"
                        >
                          <option value="none">Square (None)</option>
                          <option value="md">Rounded (MD)</option>
                          <option value="xl">Extra Rounded (XL)</option>
                          <option value="full">Pill Shape (Full)</option>
                        </select>
                      </div>
                    </div>

                    {/* Custom Background selector */}
                    <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-white/80">Custom Background Settings</span>
                        <div className="flex gap-1.5">
                          {['solid', 'gradient'].map(b => (
                            <button
                              key={b}
                              type="button"
                              onClick={() => setBgType(b as any)}
                              className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase transition-all ${
                                bgType === b ? 'bg-primary text-white' : 'bg-white/5 text-white/40'
                              }`}
                            >
                              {b}
                            </button>
                          ))}
                        </div>
                      </div>

                      {bgType === 'gradient' && (
                        <div className="grid grid-cols-2 gap-3 pt-2">
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono text-white/40 uppercase">Color 1</label>
                            <input
                              type="color"
                              value={bgColor1}
                              onChange={(e) => setBgColor1(e.target.value)}
                              className="w-full h-8 bg-transparent border-0 cursor-pointer"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono text-white/40 uppercase">Color 2</label>
                            <input
                              type="color"
                              value={bgColor2}
                              onChange={(e) => setBgColor2(e.target.value)}
                              className="w-full h-8 bg-transparent border-0 cursor-pointer"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* ════ SETTINGS TAB ════ */}
                {activeTab === 'settings' && (
                  <motion.div
                    key="tab-settings"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="space-y-6"
                  >
                    {/* Social links */}
                    <div className="space-y-4">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-white/50 flex items-center gap-1.5">
                        <Settings2 className="w-4 h-4 text-primary" /> Social Platform Handles
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[9px] font-mono text-white/40 uppercase">Twitter / X</label>
                          <input
                            type="text"
                            placeholder="username"
                            value={socialLinks.twitter}
                            onChange={(e) => setSocialLinks(prev => ({ ...prev, twitter: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs focus:outline-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-mono text-white/40 uppercase">Instagram</label>
                          <input
                            type="text"
                            placeholder="username"
                            value={socialLinks.instagram}
                            onChange={(e) => setSocialLinks(prev => ({ ...prev, instagram: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs focus:outline-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-mono text-white/40 uppercase">LinkedIn</label>
                          <input
                            type="text"
                            placeholder="username"
                            value={socialLinks.linkedin}
                            onChange={(e) => setSocialLinks(prev => ({ ...prev, linkedin: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs focus:outline-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-mono text-white/40 uppercase">Website URL</label>
                          <input
                            type="text"
                            placeholder="yoursite.com"
                            value={socialLinks.website}
                            onChange={(e) => setSocialLinks(prev => ({ ...prev, website: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {/* GeoBlocking simulation */}
                    <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl space-y-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="text-xs font-bold text-white">Geo-blocking Rules</h4>
                          <p className="text-[10px] text-white/40">Block specific regions from visiting this bio page</p>
                        </div>
                        <span className="text-[9px] font-mono font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full uppercase">
                          PRO feature
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Right Panel: Phone Preview Mockup */}
          <div className="lg:col-span-5 flex justify-center sticky top-24">
            <div className="relative w-full max-w-[280px] sm:max-w-[310px] aspect-[9/18.5] border-[8px] border-white/20 rounded-[36px] bg-black p-3 shadow-2xl flex flex-col items-center">
              {/* Speaker notch */}
              <div className="absolute top-2 w-20 h-4 bg-black rounded-b-xl z-20 flex items-center justify-center">
                <div className="w-8 h-1 bg-white/10 rounded-full" />
              </div>

              {/* Screen Area */}
              <div 
                className={`w-full h-full rounded-[28px] overflow-y-auto px-4 py-8 flex flex-col items-center transition-all duration-500 relative`}
                style={previewBg}
              >
                {/* Profile Pic Mock */}
                <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-primary to-purple-600 flex items-center justify-center text-sm font-bold border border-white/20 mt-4 shadow-lg select-none">
                  {displayName.substring(0, 3).toUpperCase().replace('@', '') || 'CK'}
                </div>

                <h3 className="text-sm font-bold mt-3 tracking-tight" style={{ color: textColor }}>{displayName || '@yourhandle'}</h3>
                <p className="text-[10px] mt-1 max-w-[180px] text-center font-light leading-relaxed opacity-60" style={{ color: textColor }}>{bio || 'Build your bio page.'}</p>

                {/* Social Icons Mock */}
                <div className="flex gap-3 mt-4 text-white/40" style={{ color: textColor }}>
                  {socialLinks.instagram && <Instagram className="w-3.5 h-3.5 hover:text-white transition-colors cursor-pointer" />}
                  {socialLinks.twitter && <Twitter className="w-3.5 h-3.5 hover:text-white transition-colors cursor-pointer" />}
                  {socialLinks.linkedin && <Linkedin className="w-3.5 h-3.5 hover:text-white transition-colors cursor-pointer" />}
                  {socialLinks.website && <Globe className="w-3.5 h-3.5 hover:text-white transition-colors cursor-pointer" />}
                </div>

                {/* Render Bio Links */}
                <div className="w-full space-y-2.5 mt-6 mb-4">
                  {bioLinks.map((link) => (
                    <div
                      key={link.id}
                      className={`w-full py-2.5 px-4 text-center text-[11px] font-bold transition-all hover:scale-[1.02] cursor-pointer flex items-center justify-center gap-1.5 select-none shadow-sm`}
                      style={{ ...getPreviewButtonStyle(), borderRadius: previewRadius }}
                    >
                      <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                      {link.title}
                    </div>
                  ))}
                  {bioLinks.length === 0 && (
                    <div className="text-center text-[10px] text-white/20 py-8 font-light">
                      No blocks added. Use the editor to insert links!
                    </div>
                  )}
                </div>

                {/* Branding footer */}
                <div className="mt-auto pt-4 flex items-center gap-1 select-none">
                  <span className="text-[8px] text-white/30 uppercase tracking-widest">Powered by</span>
                  <span className="text-[8px] font-bold tracking-tighter text-white">Lenklyst</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-20 border-t border-white/5 relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <LayoutTemplate className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Pixel-Perfect Customization</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Design bio pages that stand out. Adjust text colors, borders, custom graphics, and social links to match your brand style guidelines.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Zero Commissions on Sales</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Accept digital product orders directly from your bio page with integrated storefront buttons. We charge 0% platform transaction fees.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Instant SEO Setup</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Configure search indexing keywords, titles, and social share previews. Make your bio page rank under your own brand name organically.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-24 border-t border-white/5 text-center relative z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
        <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">Own your online presence</h2>
        <p className="text-white/40 text-sm max-w-md mx-auto mt-4 font-light">Create a beautiful, SEO-optimized, custom branded social portal in minutes.</p>
        <Link 
          href="/auth/sign-up" 
          className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 bg-primary text-white font-bold rounded-full hover:scale-105 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] active:scale-95"
        >
          Build Your Bio Link <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <MarketingFooter />
    </div>
  )
}
